import * as React from "react";
import { useState, useEffect } from "react";
import Button from "./button";
import { getFileStorageUrlSync } from "@/utils/storage";
import LevelProgressBar from "./level-progress-bar";
import { LevelUpMessage } from "./level-up-message";
import { CollectionDataCache } from "@/types";
import {
  calcLevelByExperience,
  getExperienceToLevel,
} from "@/utils/experience";

interface CollectionSuccessMessageProps {
  className?: string;
  collectionName: string;
  collectionImage: string;
  collectionData?: CollectionDataCache;
  previousExperience: number;
  onClose: () => void;
  onClick: () => void;
}

export default function CollectionSuccessMessage({
  className,
  collectionName,
  collectionImage,
  collectionData,
  previousExperience,
  onClose,
  onClick,
}: CollectionSuccessMessageProps) {
  const [showLevelUpMessage, setShowLevelUpMessage] = useState<boolean>(false);
  const [animationPhase, setAnimationPhase] = useState<"phase1" | "phase2">(
    "phase1"
  );

  const exp = collectionData ? Number(collectionData.experience) || 0 : 0;
  const calculatedCurrentExperience = Number(previousExperience) + Number(exp);
  const previousLevel = calcLevelByExperience(previousExperience);
  const currentLevel = calcLevelByExperience(calculatedCurrentExperience);
  const hasLeveledUp = previousLevel < currentLevel;

  const levelThreshold = hasLeveledUp
    ? getExperienceToLevel(previousLevel + 1)
    : getExperienceToLevel(currentLevel + 1);

  const phase1CurrentExp = hasLeveledUp
    ? levelThreshold - 1
    : calculatedCurrentExperience;
  const phase1XpGain = hasLeveledUp ? phase1CurrentExp - previousExperience : 0;

  const phase2CurrentExp = calculatedCurrentExperience;
  const phase2XpGain = exp;

  const displayCurrentExp = !hasLeveledUp
    ? calculatedCurrentExperience
    : hasLeveledUp && animationPhase === "phase1"
      ? phase1CurrentExp
      : phase2CurrentExp;

  const displayXpGain =
    exp === 0
      ? 0
      : !hasLeveledUp
        ? exp
        : hasLeveledUp && animationPhase === "phase1"
          ? phase1XpGain
          : phase2XpGain;

  const handleReach100 = () => {
    if (hasLeveledUp && animationPhase === "phase1") {
      setShowLevelUpMessage(true);
    }
  };

  const handleLevelUpMessageClick = () => {
    setShowLevelUpMessage(false);
    setAnimationPhase("phase2");
  };

  return (
    <>
      {showLevelUpMessage && (
        <LevelUpMessage
          level={currentLevel}
          onClick={handleLevelUpMessageClick}
        />
      )}

      <div
        className={`${className} ${showLevelUpMessage ? "blur-[4px] pointer-events-none" : "opacity-100 pointer-events-auto"}`}
      >
        <div className="relative min-h-[541px]">
          <header className="relative flex justify-center items-center pl-[20px] pr-[20px] pt-[12px] pb-[12px] bg-[#FE9400] ">
            <div className="text-[28px] text-center">
              <span>CONGRATULATION!</span>
            </div>
            <div
              className="absolute right-[20px] w-[21px] h-[29px] hover:cursor-pointer"
              onClick={onClose}
            >
              <img src="/assets/cancel.svg" alt="cancel" />
            </div>
          </header>
          <main className="flex flex-col justify-center items-center pt-[28px] pb-[20px] pl-[54px] pr-[54px] text-[24px] ">
            <div className="w-[145px] aspect-square relative">
              <img src={getFileStorageUrlSync(collectionImage)} alt="avatar" />
              <div className="absolute top-0 left-0 w-[145px] aspect-square  flex justify-center items-center">
                <img src="/assets/avatar-bg.png" alt="index" />
              </div>
            </div>
            <div className="pt-[20px] font-spline-sans-mono text-center">
              <p>YOUR COLLECTION:</p>
              <p className="text-[24px] font-spline-sans-mono">
                "<span className="text-[#0099FE]">{collectionName}</span>"
              </p>
              <p>successfully created!</p>
              {/* <p className="pt-[20px]">
              Go to the collection page to complete customization
            </p> */}
            </div>

            {exp > 0 && (
              <div className="pt-[30px] w-full">
                <LevelProgressBar
                  currentExperience={displayCurrentExp}
                  hasLeveledUp={hasLeveledUp && animationPhase === "phase2"}
                  xpGain={displayXpGain}
                  onReach100={handleReach100}
                />
              </div>
            )}

            <div className="pt-[40px] w-full">
              <Button
                className="w-full custom-aspect-320-63 text-[22px] hover:cursor-pointer hover:scale-105 transition-transform duration-300 ease-in-out"
                image="/assets/button.svg"
                text="GO TO COLLECTION"
                onClick={() => {
                  onClick();
                }}
              />
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
